<template>
  <div>
    <div v-if="contractDistribution && contractDistribution.contracts" class="mt-4">
      <ShowPageHeader
        backLabel="Offres"
        :backRoute="backRoute"
        :title="$t(`contract_distributions.category.${contractDistribution.category}`)"
      >
        <template #actions>
          <SecondaryButton v-if="contractDistribution.summaryUrl" @click="summaryModalOpen = true">
            Résumé
          </SecondaryButton>
          <SecondaryButton v-if="contractDistribution.kitLink" @click="openKitLink">Voir le kit</SecondaryButton>
          <SecondaryButton v-if="contractDistribution.kitLink" @click="handleOrderKit">
            Commander un kit
          </SecondaryButton>
        </template>
      </ShowPageHeader>

      <Modal :open="summaryModalOpen" @close="summaryModalOpen = false" size="big">
        <img
          v-if="summaryModalOpen"
          :src="contractDistribution.summaryUrl"
          alt="Résumé de l'offre"
          class="w-full rounded-sm"
        />
      </Modal>

      <div class="mt-6">
        <div class="bg-white rounded-2xl shadow-md p-6 mx-auto">
          <div
            v-if="hasUniformData && filteredPricingRows.length > 0"
            class="grid grid-cols-1 pb-4 mb-4 gap-y-4 sm:gap-y-0 sm:gap-x-0 border-b border-gray-100"
            :class="highlightGridCols"
          >
            <div v-if="uniformCoverages" class="sm:pr-4 sm:border-r border-gray-100">
              <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Garanties</span>
              <div class="mt-1 flex flex-wrap gap-1">
                <span
                  v-for="coverage in filteredPricingRows[0].coverages"
                  :key="coverage.name"
                  :class="getCoverageClass(coverage.name)"
                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                >
                  {{ $t(`coverage_name.${coverage.name}`) }}
                </span>
              </div>
            </div>
            <div v-if="uniformClaimLimits" class="sm:px-4 sm:border-r border-gray-100">
              <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Prise en charge</span>
              <div class="mt-1 text-sm font-bold text-gray-900">
                <div v-for="(limit, index) in filteredPricingRows[0].claimLimits" :key="index">
                  {{ limit.description }}
                </div>
                <span v-if="filteredPricingRows[0].claimLimits.length === 0">—</span>
              </div>
            </div>
            <div v-if="uniformDeductibles" class="sm:px-4 sm:border-r border-gray-100">
              <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Franchise</span>
              <div class="mt-1">
                <template v-if="filteredPricingRows[0].deductibles.length === 0">
                  <p class="text-sm font-bold text-gray-900">Pas de franchise</p>
                </template>
                <div
                  v-for="(d, index) in filteredPricingRows[0].deductibles"
                  :key="index"
                  class="flex items-center gap-1 mb-1"
                >
                  <span
                    :class="getCoverageClass(d.coverageName)"
                    class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ $t(`coverage_name.${d.coverageName}`) }}
                  </span>
                  <span class="text-sm font-bold text-gray-900">{{ d.value }}</span>
                </div>
              </div>
            </div>
            <div class="sm:pl-4">
              <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Vétusté</span>
              <p class="mt-1 text-sm font-bold text-gray-900">{{ depreciationLabel }}</p>
            </div>
          </div>

          <Pills v-model="selectedDuration" :options="availableDurations" class="mb-4" />
          <Table :columns="pricingColumns" :data="filteredPricingRows">
            <template #coverages="{ row }">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="coverage in row.coverages"
                  :key="coverage.name"
                  :class="getCoverageClass(coverage.name)"
                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                >
                  {{ $t(`coverage_name.${coverage.name}`) }}
                </span>
              </div>
            </template>
            <template #claimLimits="{ row }">
              <div class="text-xs text-gray-700">
                <div v-for="(limit, index) in row.claimLimits" :key="index">
                  {{ limit.description }}
                </div>
              </div>
            </template>
            <template #deductibles="{ row }">
              <div class="text-xs text-gray-700">
                <template v-if="row.deductibles.length === 0">Pas de franchise</template>
                <div v-for="(d, index) in row.deductibles" :key="index" class="flex items-center gap-1 mb-1">
                  <span
                    :class="getCoverageClass(d.coverageName)"
                    class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ $t(`coverage_name.${d.coverageName}`) }}
                  </span>
                  <span>{{ d.value }}</span>
                </div>
              </div>
            </template>
            <template #price="{ row }">
              <span class="text-right">{{ formatPrice(row.price) }}{{ row.monthly ? " / mois" : "" }}</span>
            </template>
          </Table>

          <div
            class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 mt-6 pt-6 border-t border-gray-100"
          >
            <div class="space-y-2">
              <StackedField label="Documents contractuels">
                <div class="flex flex-col gap-2">
                  <LinkField
                    v-if="contractDistribution.contractualDocuments?.ipid"
                    :href="contractDistribution.contractualDocuments.ipid"
                    icon="document"
                    label="IPID"
                  />
                  <LinkField
                    v-if="contractDistribution.contractualDocuments?.cg"
                    :href="contractDistribution.contractualDocuments.cg"
                    icon="document"
                    label="Conditions générales"
                  />
                  <span
                    v-if="
                      !contractDistribution.contractualDocuments?.ipid && !contractDistribution.contractualDocuments?.cg
                    "
                  >
                    —
                  </span>
                </div>
              </StackedField>
            </div>

            <div class="space-y-2">
              <template v-if="sameEngagement">
                <StackedField label="Engagement" :value="contractDistribution.cancellationPolicy.monthly" />
              </template>
              <template v-else>
                <StackedField label="Engagement mensuel" :value="contractDistribution.cancellationPolicy.monthly" />
                <StackedField label="Engagement annuel" :value="contractDistribution.cancellationPolicy.yearly" />
              </template>
              <StackedField v-if="!hasUniformData" label="Vétusté" :value="depreciationLabel" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Loading />
    </div>
  </div>
</template>

<script>
import {
  Table,
  Loading,
  SecondaryButton,
  Modal,
  ShowPageHeader,
  StackedField,
  LinkField,
  Pills,
  formatPrice,
  getCoverageColorClass,
  formatDeductibles,
  computeDepreciationLabel,
} from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";

export default {
  components: { Table, Loading, SecondaryButton, Modal, ShowPageHeader, StackedField, LinkField, Pills },

  data: () => ({
    contractDistribution: null,
    summaryModalOpen: false,
    selectedDuration: null,
    matchedContracts: null,
  }),

  computed: {
    ...mapGetters("store", ["selectedStoreId"]),

    backRoute() {
      return this.$route.path.replace(/\/[^/]+$/, "");
    },
    hasUniformData() {
      return this.uniformCoverages || this.uniformClaimLimits || this.uniformDeductibles;
    },
    highlightGridCols() {
      let count = [this.uniformCoverages, this.uniformClaimLimits, this.uniformDeductibles].filter(Boolean).length;
      count += 1;
      const colsMap = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };
      return colsMap[count] || "sm:grid-cols-4";
    },
    uniformCoverages() {
      if (this.filteredPricingRows.length === 0) return false;
      const first = JSON.stringify(this.filteredPricingRows[0].coverages.map((c) => c.name).sort());
      return this.filteredPricingRows.every((row) => JSON.stringify(row.coverages.map((c) => c.name).sort()) === first);
    },
    uniformDeductibles() {
      if (this.filteredPricingRows.length === 0) return false;
      const serialize = (deductibles) =>
        JSON.stringify([...deductibles].map((d) => `${d.coverageName}:${d.value}`).sort());
      const first = serialize(this.filteredPricingRows[0].deductibles);
      return this.filteredPricingRows.every((row) => serialize(row.deductibles) === first);
    },
    uniformClaimLimits() {
      if (this.filteredPricingRows.length === 0) return false;
      const serialize = (limits) => JSON.stringify(limits.map((l) => l.description).sort());
      const first = serialize(this.filteredPricingRows[0].claimLimits);
      return this.filteredPricingRows.every((row) => serialize(row.claimLimits) === first);
    },
    sameEngagement() {
      const policy = this.contractDistribution?.cancellationPolicy;
      return policy && policy.monthly === policy.yearly;
    },
    depreciationLabel() {
      return computeDepreciationLabel(this.contractDistribution?.depreciation);
    },
    coveragesList() {
      if (!this.contractDistribution?.contracts) return [];
      const all = this.contractDistribution.contracts.flatMap((c) => (c.coverages || []).map((cov) => cov.name));
      return [...new Set(all)];
    },
    pricingColumns() {
      const columns = [{ field: "priceBand", label: "Tranche de prix" }];
      if (!this.uniformCoverages) {
        columns.push({ field: "coverages", label: "Garanties", slot: "coverages" });
      }
      if (!this.uniformClaimLimits) {
        columns.push({ field: "claimLimits", label: "Prise en charge", slot: "claimLimits" });
      }
      if (!this.uniformDeductibles) {
        columns.push({ field: "deductibles", label: "Franchise", slot: "deductibles" });
      }
      columns.push({ field: "price", label: "Prix", slot: "price" });

      const hasStoreCommission = this.matchedContracts?.some((mc) => mc.storeCommissionAmount != null);
      const hasAnnualizedCommission = this.matchedContracts?.some((mc) => mc.annualizedStoreCommissionAmount != null);
      const hasWholesalePrice = this.matchedContracts?.some((mc) => mc.wholesalePrice != null);

      if (hasStoreCommission) {
        columns.push({ field: "storeCommissionAmount", label: "Commission", price: true });
      }
      if (hasAnnualizedCommission) {
        columns.push({ field: "annualizedStoreCommissionAmount", label: "Commission annualisée", price: true });
      }
      if (hasWholesalePrice) {
        columns.push({ field: "wholesalePrice", label: "Prix d'achat", price: true });
      }

      return columns;
    },
    pricingRows() {
      if (!this.contractDistribution?.contracts || !this.matchedContracts) return [];
      return this.contractDistribution.contracts.map((contract) => {
        const matchedContract = this.matchedContracts?.find((mc) => mc.contract.id === contract.id);
        return {
          priceBand: `de ${formatPrice(contract.priceBandMin)} à ${formatPrice(contract.priceBandMax)}`,
          duration: contract.monthly ? "Mensuel" : `One shot - ${contract.termLength} mois`,
          price: matchedContract?.price ?? contract.defaultPrice,
          monthly: contract.monthly,
          coverages: contract.coverages || [],
          claimLimits: contract.claimLimits || [],
          deductibles: formatDeductibles(contract.coverages || []),
          storeCommissionAmount: matchedContract?.storeCommissionAmount,
          annualizedStoreCommissionAmount: matchedContract?.annualizedStoreCommissionAmount,
          wholesalePrice: matchedContract?.wholesalePrice,
        };
      });
    },
    availableDurations() {
      return [...new Set(this.pricingRows.map((row) => row.duration))];
    },
    filteredPricingRows() {
      if (!this.selectedDuration) return this.pricingRows;
      return this.pricingRows.filter((row) => row.duration === this.selectedDuration);
    },
  },

  watch: {
    availableDurations: {
      immediate: true,
      handler(durations) {
        if (durations.length > 0 && !durations.includes(this.selectedDuration)) {
          this.selectedDuration = durations[0];
        }
      },
    },
  },

  async mounted() {
    await this.fetchContractDistribution();
    await this.fetchMatchedContractsData();
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("contractDistributions", ["fetch", "orderKit"]),
    ...mapActions("matchedContracts", { fetchMatchedContracts: "fetchIndex" }),

    formatPrice,

    openKitLink() {
      window.open(this.contractDistribution.kitLink, "_blank", "noopener,noreferrer");
    },
    async handleOrderKit() {
      if (!window.confirm("Êtes-vous sûr de vouloir commander un kit ?")) return;

      const response = await this.orderKit({
        id: this.$route.params.id,
        storeId: this.selectedStoreId,
      });

      if (response?.success) {
        this.notify({
          category: "simple",
          type: "success",
          title: "Kit commandé avec succès",
        });
      }
    },

    async fetchContractDistribution() {
      const includes = [
        "contractual_documents",
        "cancellation_policy",
        "depreciation",
        "claim_limits",
        "contracts",
        "coverages",
      ];
      this.contractDistribution = await this.fetch({ id: this.$route.params.id, includes });
    },

    async fetchMatchedContractsData() {
      const response = await this.fetchMatchedContracts({
        storeId: this.selectedStoreId,
        contractDistributionId: this.$route.params.id,
        page: 1,
        perPage: 500,
        includes: ["contract"],
      });
      this.matchedContracts = response?.matchedContracts;
    },

    getCoverageClass(coverageName) {
      return getCoverageColorClass(coverageName, this.coveragesList);
    },
  },
};
</script>
